import React, { useState, useEffect } from 'react';
import { SystemConfig, Teacher, Student } from '../../data/types';
import { dataService } from '../../services/dataService';

interface ConcurrentAccessTabProps {
  config: SystemConfig;
  onSave: (updatedConfig: Partial<SystemConfig>) => void;
  saving: boolean;
}

const ConcurrentAccessTab: React.FC<ConcurrentAccessTabProps> = ({ config, onSave, saving }) => {
  const [settings, setSettings] = useState({
    defaultTimeout: config.concurrentAccess.defaultTimeout,
    requireJustification: config.concurrentAccess.requireJustification,
    logAllActions: config.concurrentAccess.logAllActions
  });
  
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState<'teacher' | 'student'>('teacher');
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [accessMode, setAccessMode] = useState<'ReadOnly' | 'FullAccess'>('ReadOnly');
  const [purpose, setPurpose] = useState<string>('');
  const [accessStatus, setAccessStatus] = useState<'idle' | 'starting' | 'active' | 'error'>('idle');
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const [teachersData, studentsData] = await Promise.all([
          dataService.getTeachers(),
          dataService.getStudents()
        ]);
        
        setTeachers(teachersData);
        setStudents(studentsData);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleSave = () => {
    const updatedConfig: Partial<SystemConfig> = {
      concurrentAccess: settings
    };
    
    onSave(updatedConfig);
  };

  const handleStartAccess = async () => {
    if (!selectedUser || (settings.requireJustification && !purpose)) {
      return;
    }
    
    try {
      setAccessStatus('starting');
      await dataService.startConcurrentAccess(selectedUser, accessMode, purpose);
      setAccessStatus('active');
      
      // In a real app, this would open a new browser tab with the user's view
      window.open('#', '_blank');
    } catch (error) {
      console.error('Error starting concurrent access:', error);
      setAccessStatus('error');
      
      // Reset after 3 seconds
      setTimeout(() => setAccessStatus('idle'), 3000);
    }
  };

  const handleEndAccess = async () => {
    try {
      await dataService.endConcurrentAccess(selectedUser);
      setAccessStatus('idle');
      setSelectedUser('');
      setPurpose('');
    } catch (error) {
      console.error('Error ending concurrent access:', error);
    }
  };

  // Filter users based on search query
  const filteredUsers = userType === 'teacher'
    ? teachers.filter(teacher => 
        `${teacher.firstName} ${teacher.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : students.filter(student => 
        `${student.firstName} ${student.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
      );

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-800 mb-4">Concurrent Access</h2>
      <p className="text-gray-600 mb-6">
        This feature allows you to temporarily access the platform as a teacher or student
        to troubleshoot issues, verify configurations, or understand the user experience.
      </p>
      
      {/* Settings */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-700 mb-3">Concurrent Access Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Default Session Timeout (Minutes)
            </label>
            <input
              type="number"
              min="5"
              max="120"
              className="w-full border border-gray-300 rounded px-3 py-2"
              value={settings.defaultTimeout}
              onChange={(e) => setSettings({
                ...settings,
                defaultTimeout: parseInt(e.target.value) || 30
              })}
            />
            <p className="text-xs text-gray-500 mt-1">
              Concurrent access sessions will automatically expire after this period
            </p>
          </div>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="requireJustification"
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
              checked={settings.requireJustification}
              onChange={() => setSettings({
                ...settings,
                requireJustification: !settings.requireJustification
              })}
            />
            <label htmlFor="requireJustification" className="ml-2 block text-sm text-gray-700">
              Require justification for concurrent access
            </label>
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="logAllActions"
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
              checked={settings.logAllActions}
              onChange={() => setSettings({
                ...settings,
                logAllActions: !settings.logAllActions
              })}
            />
            <label htmlFor="logAllActions" className="ml-2 block text-sm text-gray-700">
              Log all actions performed during concurrent access
            </label>
          </div>
        </div>
        
        <div className="flex justify-end mt-4">
          <button
            className={`px-4 py-2 rounded text-white ${
              saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary hover:bg-primary-dark'
            }`}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
      
      {/* Access Interface */}
      <div className="border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Start Concurrent Access</h3>
        
        {accessStatus === 'error' && (
          <div className="mb-4 p-3 rounded bg-red-50 text-red-700">
            Error starting concurrent access. Please try again.
          </div>
        )}
        
        {accessStatus === 'active' ? (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <div className="flex justify-between items-center">
              <div>
                <h4 className="font-medium text-blue-700">Active Concurrent Access Session</h4>
                <p className="text-sm text-blue-600">
                  You are currently accessing the platform as {
                    userType === 'teacher' ? 
                      teachers.find(t => t.id === selectedUser)?.firstName + ' ' + teachers.find(t => t.id === selectedUser)?.lastName :
                      students.find(s => s.id === selectedUser)?.firstName + ' ' + students.find(s => s.id === selectedUser)?.lastName
                  } ({userType}) in {accessMode} mode.
                </p>
              </div>
              <button
                className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                onClick={handleEndAccess}
              >
                End Session
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-4">
              <div className="flex space-x-4 mb-4">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="teacherType"
                    name="userType"
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                    checked={userType === 'teacher'}
                    onChange={() => {
                      setUserType('teacher');
                      setSelectedUser('');
                    }}
                  />
                  <label htmlFor="teacherType" className="ml-2 block text-sm text-gray-700">
                    Teacher
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="studentType"
                    name="userType"
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                    checked={userType === 'student'}
                    onChange={() => {
                      setUserType('student');
                      setSelectedUser('');
                    }}
                  />
                  <label htmlFor="studentType" className="ml-2 block text-sm text-gray-700">
                    Student
                  </label>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search for a {userType}
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder={`Search by name...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              <div className="mb-4 max-h-60 overflow-y-auto border border-gray-200 rounded">
                {loading ? (
                  <div className="flex items-center justify-center h-20">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    No {userType}s found matching your search
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {filteredUsers.map(user => (
                      <div
                        key={user.id}
                        className={`p-3 hover:bg-gray-50 cursor-pointer ${
                          selectedUser === user.id ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => setSelectedUser(user.id)}
                      >
                        <div className="flex items-center">
                          {user.avatar ? (
                            <img src={user.avatar} alt={user.firstName} className="h-8 w-8 rounded-full mr-3" />
                          ) : (
                            <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                              <span className="text-gray-600 text-xs font-bold">
                                {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                              </span>
                            </div>
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {user.firstName} {user.lastName}
                            </div>
                            <div className="text-xs text-gray-500">
                              {userType === 'teacher' ? 
                                `Grade: ${(user as Teacher).grade}` : 
                                `Grade: ${(user as Student).grade}`
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Access Mode
              </label>
              <div className="flex space-x-4">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="readOnlyMode"
                    name="accessMode"
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                    checked={accessMode === 'ReadOnly'}
                    onChange={() => setAccessMode('ReadOnly')}
                  />
                  <label htmlFor="readOnlyMode" className="ml-2 block text-sm text-gray-700">
                    Read-Only Mode
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="fullAccessMode"
                    name="accessMode"
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                    checked={accessMode === 'FullAccess'}
                    onChange={() => setAccessMode('FullAccess')}
                  />
                  <label htmlFor="fullAccessMode" className="ml-2 block text-sm text-gray-700">
                    Full Access Mode
                  </label>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Read-Only Mode allows you to view the interface without making changes.
                Full Access Mode allows you to interact with the platform as the selected user.
              </p>
            </div>
            
            {settings.requireJustification && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Purpose of Access
                </label>
                <textarea
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  rows={3}
                  placeholder="Explain why you need to access the platform as this user..."
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                ></textarea>
              </div>
            )}
            
            {accessMode === 'FullAccess' && (
              <div className="mb-4 p-3 bg-yellow-50 text-yellow-700 rounded">
                <p className="text-sm font-medium">Warning: Full Access Mode</p>
                <p className="text-xs">
                  In Full Access Mode, all actions you take will be performed as the selected user.
                  These actions will affect the user's data and may impact their experience.
                  All actions will be logged in the audit system.
                </p>
              </div>
            )}
            
            <div className="flex justify-end">
              <button
                className={`px-4 py-2 rounded text-white ${
                  !selectedUser || (settings.requireJustification && !purpose) || accessStatus === 'starting'
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-primary hover:bg-primary-dark'
                }`}
                onClick={handleStartAccess}
                disabled={!selectedUser || (settings.requireJustification && !purpose) || accessStatus === 'starting'}
              >
                {accessStatus === 'starting' ? 'Starting...' : 'Start Concurrent Access'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ConcurrentAccessTab;
